import type { ClientGame, ClientGameCtx } from "./index";
import { TET, TET_PIECES } from "../../shared/arcade";
import { sound } from "../sound";
import { countdownOverlay } from "./asteroids";
import { C, TINT, displayFont, bodyFont, hudPill } from "../theme";
import { roundRectPath, cachedGrad } from "../draw";
import { safeTop, hudRowY, HUD_PILL, HUD_PILL_H, boardPanel, boardOutline } from "./snake";

const COLS = TET.COLS;
const ROWS = TET.ROWS;

// I J L O S T Z  + garbage
const COLORS = [C.mint, C.cobalt, "#FF9A3D", C.mango, C.green, C.grape, C.coral];
const GARB = C.faint;
const SIDE_W = 76; // solo side column (next / score / lines / level)

export class TetrisClient implements ClientGame {
  private ctx!: ClientGameCtx;
  private canvas!: HTMLCanvasElement;
  private pad!: HTMLDivElement;
  private raf = 0;
  private timers: number[] = [];

  private get st() { return this.ctx.room.state.tetris; }

  mount(ctx: ClientGameCtx) {
    this.ctx = ctx;
    sound.unlock();
    this.canvas = document.createElement("canvas");
    this.canvas.id = "game-canvas";
    ctx.root.appendChild(this.canvas);
    this.buildPad(ctx.root);

    ctx.room.onMessage("t-lock", (m: any) => { if (m?.slot === ctx.mySlot) sound.place(); });
    ctx.room.onMessage("t-rot", (m: any) => { if (m?.slot === ctx.mySlot) sound.key(); });
    ctx.room.onMessage("t-drop", (m: any) => { if (m?.slot === ctx.mySlot) sound.tetHardDrop(); });
    ctx.room.onMessage("t-clear", (m: any) => {
      if (m?.slot === ctx.mySlot) sound.tetClear(m?.rows ?? 1);
      else sound.tetGarbageIn();
    });
    ctx.room.onMessage("t-garbage", (m: any) => { if (m?.slot === ctx.mySlot) sound.tetGarbageIn(); });
    ctx.room.onMessage("t-level", (m: any) => { if (m?.slot === ctx.mySlot) sound.levelUp(); });

    window.addEventListener("keydown", this.onKey);
    this.loop();
  }

  unmount() {
    cancelAnimationFrame(this.raf);
    this.timers.forEach((t) => clearInterval(t));
    this.timers = [];
    window.removeEventListener("keydown", this.onKey);
    this.pad?.remove();
  }

  // ---- controls ----------------------------------------------------------

  private send(type: string) {
    if (this.ctx.room.state.phase === "playing") this.ctx.room.send(type);
  }

  private buildPad(root: HTMLElement) {
    this.pad = document.createElement("div");
    this.pad.className = "tet-pad";

    const mkBtn = (label: string, type: string, repeat: boolean) => {
      const b = document.createElement("button");
      b.className = `tet-btn tb-${type}`;
      b.innerHTML = label;
      b.setAttribute("aria-label", type);
      let hold = 0;
      let delay = 0;
      const start = (e: Event) => {
        e.preventDefault();
        b.classList.add("on");
        this.send(type);
        if (repeat) {
          delay = window.setTimeout(() => {
            hold = window.setInterval(() => this.send(type), type === "down" ? 45 : 55);
            this.timers.push(hold);
          }, 150);
          this.timers.push(delay);
        }
      };
      const end = (e: Event) => {
        e.preventDefault();
        b.classList.remove("on");
        clearTimeout(delay);
        clearInterval(hold);
      };
      b.addEventListener("touchstart", start, { passive: false });
      b.addEventListener("touchend", end);
      b.addEventListener("touchcancel", end);
      b.addEventListener("mousedown", start);
      b.addEventListener("mouseup", end);
      b.addEventListener("mouseleave", end);
      return b;
    };

    const dpad = document.createElement("div");
    dpad.className = "tet-dpad";
    dpad.append(
      mkBtn("&#9650;", "hard", false),   // up  = hard drop
      mkBtn("&#9664;", "left", true),
      mkBtn("&#9654;", "right", true),
      mkBtn("&#9660;", "down", true),    // down = soft drop
    );
    const rot = mkBtn("&#8635;", "rotate", false);
    rot.classList.add("tet-rotate");

    this.pad.append(dpad, rot);
    root.appendChild(this.pad);
  }

  private onKey = (e: KeyboardEvent) => {
    const map: Record<string, string> = {
      ArrowLeft: "left", ArrowRight: "right", ArrowDown: "down",
      ArrowUp: "rotate", KeyX: "rotate", KeyZ: "rotate", Space: "hard",
    };
    const t = map[e.code];
    if (!t) return;
    e.preventDefault();
    this.send(t);
  };

  // ---- render ----------------------------------------------------------

  private loop = () => {
    try { this.draw(); } catch (e) { console.error("tetris frame", e); }
    this.raf = requestAnimationFrame(this.loop);
  };

  private draw() {
    const { room, mySlot } = this.ctx;
    const st = this.st;
    const c = this.canvas;
    const g = c.getContext("2d")!;
    const dpr = Math.min(2, devicePixelRatio || 1);
    const cw = c.clientWidth, ch = c.clientHeight;
    if (cw < 40 || ch < 40) return;
    if (c.width !== cw * dpr || c.height !== ch * dpr) { c.width = cw * dpr; c.height = ch * dpr; }
    g.save();
    g.scale(dpr, dpr);
    // a soft sky, edge to edge
    g.fillStyle = cachedGrad(`tet-bg-${ch}`, () => {
      const gr = g.createLinearGradient(0, 0, 0, ch);
      gr.addColorStop(0, TINT.cobalt);
      gr.addColorStop(1, C.bg);
      return gr;
    });
    g.fillRect(0, 0, cw, ch);

    const me = mySlot === 1 ? 1 : 0;
    const rival = me === 0 ? 1 : 0;
    // during the pre-start countdown fields aren't populated yet — show one well
    const solo = st.solo || !st.fields || st.fields.length < 2;

    const side = 12;
    const botPad = 10;
    if (solo) {
      // solo: the HUD lives in a side column, so the well only has to clear
      // the quit button / best-score banner
      const topPad = safeTop() + 62;
      const availH = ch - topPad - botPad - this.padHeight();
      const cell = Math.max(6, Math.min((cw - side * 2 - 10 - SIDE_W) / COLS, availH / ROWS));
      const bw = cell * COLS;
      const totalW = bw + 10 + SIDE_W;
      const x0 = Math.max(side, (cw - totalW) / 2);
      this.drawWell(g, st.fields?.[me], x0, topPad, cell);
      this.drawSideHud(g, st.fields?.[me], x0 + bw + 10, topPad, cell);
    } else {
      const hudY = hudRowY(false);
      const topPad = hudY + HUD_PILL_H + 14;
      const availH = ch - topPad - botPad - this.padHeight();
      const gap = 14;
      const cell = Math.max(5, Math.min((cw - side * 2 - gap) / (COLS * 2), availH / ROWS));
      const bw = cell * COLS;
      const totalW = bw * 2 + gap;
      const x0 = (cw - totalW) / 2;
      this.drawWell(g, st.fields?.[me], x0, topPad, cell);
      this.drawWell(g, st.fields?.[rival], x0 + bw + gap, topPad, cell);
      // stat pills over each well; mine starts clear of the quit button
      const fm = st.fields?.[me], fr = st.fields?.[rival];
      hudPill(g, Math.max(x0, 52), hudY, `${fm?.score ?? 0} · ${fm?.lines ?? 0}L`, { size: HUD_PILL - 2, label: "you", ink: C.ink });
      hudPill(g, x0 + totalW, hudY, `${fr?.score ?? 0} · ${fr?.lines ?? 0}L`, { align: "right", size: HUD_PILL - 2, label: "rival", ink: "#D9473A" });
    }

    if (room.state.phase === "countdown") countdownOverlay(g, cw, ch, room.state.countdown);
    g.restore();
  }

  private padHeight() {
    return this.pad?.offsetHeight ? this.pad.offsetHeight + 12 : 150;
  }

  private colorFor(ch: string): string {
    if (ch === "G") return GARB;
    const n = ch.charCodeAt(0) - 48;
    return COLORS[n] || "#888";
  }

  private drawWell(
    g: CanvasRenderingContext2D,
    f: any,
    ox: number,
    oy: number,
    cell: number,
  ) {
    const bw = cell * COLS, bh = cell * ROWS;
    const r = Math.max(6, cell * 0.4);
    // white well in an ink sticker frame, faint lilac grid
    boardPanel(g, ox, oy, bw, bh, r, C.white);
    g.strokeStyle = C.line;
    g.lineWidth = 1;
    g.beginPath();
    for (let x = 1; x < COLS; x++) { g.moveTo(ox + x * cell, oy); g.lineTo(ox + x * cell, oy + bh); }
    for (let y = 1; y < ROWS; y++) { g.moveTo(ox, oy + y * cell); g.lineTo(ox + bw, oy + y * cell); }
    g.stroke();

    if (f) {
      g.save();
      roundRectPath(g, ox, oy, bw, bh, r);
      g.clip();
      const well: string = f.well || "";
      for (let y = 0; y < ROWS; y++) {
        for (let x = 0; x < COLS; x++) {
          const chc = well[y * COLS + x];
          if (!chc || chc === ".") continue;
          this.cellRect(g, ox + x * cell, oy + y * cell, cell, this.colorFor(chc));
        }
      }

      // active piece + ghost
      if (f.pType >= 0 && !f.dead) {
        const shape = TET_PIECES[f.pType]?.[((f.pRot % 4) + 4) % 4];
        if (shape) {
          // ghost — a dashed ink outline where the piece will land
          let gy = f.pY;
          const solid = (px: number, py: number) =>
            px < 0 || px >= COLS || py >= ROWS || (py >= 0 && well[py * COLS + px] && well[py * COLS + px] !== ".");
          const fits = (yy: number) => shape.every(([cx, cy]) => !solid(f.pX + cx, yy + cy));
          while (fits(gy + 1)) gy++;
          g.globalAlpha = 0.25;
          for (const [cx, cy] of shape) {
            const yy = gy + cy;
            if (yy >= 0) { g.fillStyle = COLORS[f.pType]!; g.fillRect(ox + (f.pX + cx) * cell + 1, oy + yy * cell + 1, cell - 2, cell - 2); }
          }
          g.globalAlpha = 1;
          for (const [cx, cy] of shape) {
            const yy = f.pY + cy;
            if (yy >= 0) this.cellRect(g, ox + (f.pX + cx) * cell, oy + yy * cell, cell, COLORS[f.pType]!);
          }
        }
      }
      g.restore();
    }
    boardOutline(g, ox, oy, bw, bh, r);
    if (!f) return;

    if (f.dead) {
      g.save();
      roundRectPath(g, ox, oy, bw, bh, r);
      g.fillStyle = "rgba(27,21,52,0.35)";
      g.fill();
      const fs = Math.max(14, Math.round(cell * 1.1));
      g.font = displayFont(fs);
      const tw = g.measureText("Topped out").width;
      const pw = Math.min(bw - 8, tw + fs * 1.2), ph = fs * 1.9;
      const px = ox + (bw - pw) / 2, py = oy + (bh - ph) / 2;
      roundRectPath(g, px + 2, py + 4, pw, ph, ph / 2);
      g.fillStyle = C.ink; g.fill();
      roundRectPath(g, px, py, pw, ph, ph / 2);
      g.fillStyle = C.white; g.fill();
      g.strokeStyle = C.ink; g.lineWidth = 2.5; g.stroke();
      g.fillStyle = "#D9473A";
      g.textAlign = "center";
      g.textBaseline = "middle";
      g.fillText("Topped out", ox + bw / 2, py + ph / 2 + fs * 0.08, bw - 16);
      g.restore();
    }

    if (f.pending > 0) {
      // incoming garbage meter, just left of the well
      for (let i = 0; i < f.pending && i < ROWS; i++) {
        const y = oy + bh - (i + 1) * cell;
        g.fillStyle = C.coral;
        g.fillRect(ox - 8, y + 1, 5, cell - 2);
        g.strokeStyle = C.ink;
        g.lineWidth = 1.2;
        g.strokeRect(ox - 8, y + 1, 5, cell - 2);
      }
    }
  }

  /** one block: flat fill, light top lip, darker bottom lip, ink outline */
  private cellRect(g: CanvasRenderingContext2D, x: number, y: number, s: number, col: string) {
    g.fillStyle = col;
    g.fillRect(x + 0.5, y + 0.5, s - 1, s - 1);
    const lip = Math.max(1, s * 0.16);
    g.fillStyle = "rgba(255,255,255,0.38)";
    g.fillRect(x + 0.5, y + 0.5, s - 1, lip);
    g.fillStyle = "rgba(27,21,52,0.18)";
    g.fillRect(x + 0.5, y + s - 0.5 - lip, s - 1, lip);
    g.strokeStyle = C.ink;
    g.lineWidth = Math.max(1.2, s * 0.07);
    g.strokeRect(x + 1, y + 1, s - 2, s - 2);
  }

  /** a small white sticker card for the solo side column */
  private sideCard(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number) {
    roundRectPath(g, x + 2, y + 4, w, h, 12);
    g.fillStyle = C.ink; g.fill();
    roundRectPath(g, x, y, w, h, 12);
    g.fillStyle = C.white; g.fill();
    g.strokeStyle = C.ink; g.lineWidth = 2.5; g.lineJoin = "round"; g.stroke();
  }

  private drawSideHud(g: CanvasRenderingContext2D, f: any, x: number, y: number, cell: number) {
    if (!f) return;
    const w = SIDE_W;
    g.textAlign = "center";
    g.textBaseline = "top";
    // next piece card
    this.sideCard(g, x, y, w, 80);
    g.fillStyle = C.muted;
    g.font = bodyFont(11, 900);
    g.fillText("Next", x + w / 2, y + 7);
    const shape = TET_PIECES[f.nextType]?.[0];
    if (shape) {
      const nc = Math.min(cell * 0.7, 14);
      let minX = 9, maxX = -9, minY = 9, maxY = -9;
      for (const [px, py] of shape) { minX = Math.min(minX, px); maxX = Math.max(maxX, px); minY = Math.min(minY, py); maxY = Math.max(maxY, py); }
      const pw = (maxX - minX + 1) * nc, ph = (maxY - minY + 1) * nc;
      const bx = x + (w - pw) / 2 - minX * nc, by = y + 26 + (46 - ph) / 2 - minY * nc;
      for (const [px, py] of shape) this.cellRect(g, bx + px * nc, by + py * nc, nc, COLORS[f.nextType]!);
    }
    // stat cards
    const stat = (label: string, val: string, yy: number) => {
      this.sideCard(g, x, yy, w, 52);
      g.textAlign = "center";
      g.textBaseline = "top";
      g.fillStyle = C.muted;
      g.font = bodyFont(11, 900);
      g.fillText(label, x + w / 2, yy + 6);
      g.fillStyle = C.ink;
      g.font = displayFont(20);
      g.fillText(val, x + w / 2, yy + 20, w - 10);
    };
    stat("Score", `${f.score}`, y + 92);
    stat("Lines", `${f.lines}`, y + 156);
    stat("Level", `${f.level}`, y + 220);
  }

  results(ctx: ClientGameCtx): HTMLElement {
    const st = ctx.room.state.tetris;
    const mine = st.fields?.[ctx.mySlot === 1 ? 1 : 0];
    const d = document.createElement("div");
    d.className = "arc-final";
    if (st.solo) {
      d.innerHTML =
        `<div class="score-big">${mine?.score ?? 0}</div>` +
        `<div class="arc-note">${mine?.lines ?? 0} lines · level ${mine?.level ?? 1}</div>`;
    } else {
      const rival = st.fields?.[ctx.mySlot === 1 ? 0 : 1];
      d.innerHTML =
        `<div class="score-big">${mine?.score ?? 0} : ${rival?.score ?? 0}</div>` +
        `<div class="arc-note">${mine?.lines ?? 0}L vs ${rival?.lines ?? 0}L</div>`;
    }
    return d;
  }
}
